import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import type { CreateReviewRequestForExistingClientBody, CreateReviewRequestForNewClientBody } from '@shopexperts/shared';


/**
 * Composable for creating review requests
 * Handles both existing and new client scenarios
 */
export function useCreateReviewRequest() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationKey: ['expert', 'createReviewRequest'],
    mutationFn: async (payload: CreateReviewRequestForExistingClientBody | CreateReviewRequestForNewClientBody) => {
      if ('client_id' in payload) {
        return api.expert.reviewsV2.createReviewRequestForExistingClient({ body: payload as CreateReviewRequestForExistingClientBody });
      } else {
        return api.expert.reviewsV2.createReviewRequestForNewClient({ body: payload as CreateReviewRequestForNewClientBody });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expert', 'review-projects'] });
      await queryClient.invalidateQueries({ queryKey: ['expert', 'review-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['expert', 'reviews'] });
      
      toaster.create({
        title: 'Review request sent successfully!',
        description: 'Your client will receive your review request by email.',
        type: 'success',
      });
    },
    onError: (error: any) => {
      console.error('[useCreateReviewRequest] Error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message ||
        'Failed to send review request. Please try again.';
      
      toaster.create({
        title: 'Error sending review request',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const isSubmitting = computed(() => createMutation.isPending.value);

  const submitReviewRequest = (payload: CreateReviewRequestForExistingClientBody | CreateReviewRequestForNewClientBody) => {
    return createMutation.mutateAsync(payload);
  };

  return {
    createMutation,
    isSubmitting,
    submitReviewRequest,
  };
}

